/**
 * 客服 / AI 伴学助手共用的腾讯云 IM 拉起逻辑（全局单例见 customerImClient）。
 * 与 all-btn 原流程 loginJudgment → getSig → createTIM 对齐，并合并并发为一次 in-flight。
 */
import $store from '../../store/index'
import {
  initCustomerIM,
  destroyCustomerIM,
  getCustomerIMInstance,
  setCustomerIMMessageHandler
} from './customerImClient'

/** @type {Promise<void> | null} */
let inflightEnsure = null

/**
 * 与 all-btn.handleLogoutOnError 中 IM 部分一致：登出并销毁全局客服 IM 实例。
 * @returns {Promise<void>}
 */
export async function destroyCustomerImOnError () {
  await destroyCustomerIM()
}

/**
 * 先 LOGIN_STATE，再拉 GET_USER_SIG / TALK_GETUSER_SIGN 并 login；全局仅一个 TIM 实例。
 * @param {object} opts
 * @param {Function} opts.limitPost (apiKey, body, onSuccess, onError?) => void
 * @param {Function} opts.post (apiKey, body, onSuccess, onError?) => void
 * @param {{ get?: Function, remove?: Function }} [opts.cookie]
 * @param {{ cache: Function }} opts.utils
 * @param {Function} [opts.onMessage] 为函数时才 setCustomerIMMessageHandler，否则保留已有 handler
 * @param {Function} [opts.onLoginState] LOGIN_STATE 回调 (res, err) => void，成功时 err 为 null
 * @returns {Promise<import('tim-js-sdk').default | null>}
 */
export async function ensureCustomerImReady (opts) {
  const { limitPost, post, cookie, utils, onMessage, onLoginState } = opts
  const timExisting = getCustomerIMInstance()
  if (timExisting && $store.state.customerIm.isLogin) {
    if (typeof onMessage === 'function') {
      setCustomerIMMessageHandler(onMessage)
    }
    return timExisting
  }

  if (inflightEnsure) {
    try {
      await inflightEnsure
    } catch (e) {
      /* 首调失败由首调方日志处理 */
    }
    if (typeof onMessage === 'function') {
      setCustomerIMMessageHandler(onMessage)
    }
    return getCustomerIMInstance()
  }

  const run = (async () => {
    await syncLoginStateForCustomerIm({ limitPost, cookie, utils, onLoginState })
    const { sdkAppId, userSig, userId } = await fetchCustomerImSig({ post, cookie, utils })
    if (!sdkAppId || !userSig || !userId) {
      throw new Error('customer im sig missing')
    }
    if (typeof onMessage === 'function') {
      setCustomerIMMessageHandler(onMessage)
    }
    await initCustomerIM({
      SDKAppID: sdkAppId,
      userID: userId,
      userSig,
      onMessage: typeof onMessage === 'function' ? onMessage : undefined
    })
  })()

  inflightEnsure = run
  try {
    await run
    return getCustomerIMInstance()
  } finally {
    inflightEnsure = null
  }
}

/**
 * @param {object} p
 * @param {Function} p.limitPost
 * @param {{ get?: Function, remove?: Function }} [p.cookie]
 * @param {{ cache: Function }} p.utils
 * @param {Function} [p.onLoginState]
 * @returns {Promise<void>}
 */
function syncLoginStateForCustomerIm (p) {
  const { limitPost, cookie, utils, onLoginState } = p
  return new Promise((resolve) => {
    limitPost(
      'LOGIN_STATE',
      {},
      (res) => {
        if (typeof onLoginState === 'function') {
          onLoginState(res, null)
        }
        if (res && res.code === 10016) {
          clearUserSession(cookie, utils)
        }
        resolve()
      },
      (err) => {
        if (typeof onLoginState === 'function') {
          onLoginState(null, err)
        }
        if (err && err.code === 10016) {
          clearUserSession(cookie, utils)
        }
        resolve()
      }
    )
  })
}

function clearUserSession (cookie, utils) {
  if (cookie && typeof cookie.remove === 'function') {
    cookie.remove('uid')
    cookie.remove('token')
  }
  if (utils && typeof utils.cache === 'function') {
    utils.cache('userData', null)
  }
}

/**
 * @param {object} p
 * @param {Function} p.post
 * @param {{ get?: Function }} [p.cookie]
 * @param {{ cache: Function }} p.utils
 * @returns {Promise<{ sdkAppId: string, userSig: string, userId: string }>}
 */
function fetchCustomerImSig (p) {
  const { post, cookie, utils } = p
  let userData = null
  try {
    const raw = utils.cache('userData')
    userData = raw ? JSON.parse(raw) : null
  } catch (e) {
    userData = null
  }
  const userIdFromData = userData && userData.userId
  const apiKey = userIdFromData ? 'GET_USER_SIG' : 'TALK_GETUSER_SIGN'
  return new Promise((resolve, reject) => {
    post(
      apiKey,
      {},
      (res) => {
        const d = (res && res.data) || {}
        const userSig = d.userSig || ''
        const sdkAppId = d.sdkAppId || ''
        const bSessionId = cookie && typeof cookie.get === 'function' ? cookie.get('bSessionId') : ''
        const userId = userIdFromData || bSessionId || ''
        resolve({ sdkAppId, userSig, userId })
      },
      (err) => {
        reject(err || new Error('GET_USER_SIG failed'))
      }
    )
  })
}



// WEBPACK FOOTER //
// ./src/assets/js/customerImBootstrap.js